import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { findCompanyContactByEmail, isCheckableEmail } from '@/lib/enquiry-contact';

/**
 * The Actions column on the three Enquiries sections — Event Interest
 * Submissions, Proposals and Report leads.
 *
 * Owner's decision 2026-10-01: ONE "Actions" menu per row instead of stacked
 * bordered buttons whose labels wrapped onto two lines. The status facts that
 * are links — the attached company and the deal — stay visible as one-line text
 * above the menu, because a fact hidden inside a menu is a fact nobody sees.
 *
 * ⚠️ **ONE COMPONENT FOR ALL THREE SECTIONS**, so they cannot diverge. Every
 * GATE stays with the caller, which builds the item list: a withheld action is
 * simply absent from `items`, exactly as the button it replaced was withheld.
 * An empty list renders no trigger — a menu that opens on nothing is an
 * affordance with one outcome.
 */

/** One item in a row's Actions menu. */
export interface EnquiryActionItem {
  key: string;
  label: string;
  onSelect: () => void;
  /** Toned danger — the permanent delete. */
  danger?: boolean;
  /** This action is in flight; the trigger shows the spinner. */
  busy?: boolean;
  disabled?: boolean;
}

/** The deal fact shown above the menu, when there is one to state. */
export type EnquiryDealStatus = { kind: 'raised'; id: number } | { kind: 'deleted' };

/** The column width the three tables reserve for this cell, padding included. */
export const ENQUIRY_ACTIONS_COLUMN_WIDTH = 168;

const MENU_WIDTH = 200;
/** Used only until the real menu has been measured, to pick the first side. */
const MENU_ITEM_ESTIMATE = 40;
const MENU_ITEMS = '[role="menuitem"]:not(:disabled)';
const GUTTER = 8;

function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * The menu itself: one `Actions ▾` button and a portalled `role="menu"`.
 *
 * ⚠️ **Portalled to the body, `position: fixed`, on the `z-[60]` portalled-
 * popover rung** — every mount sits inside a `TableWrap` (whose
 * `overflow-x-auto` clips an absolutely-positioned child) and inside a pinned
 * `sticky right-0` cell (a stacking context), so anything else is clipped or
 * painted under. Same pattern as `RecordCreateMenu`, which it copies for the
 * keyboard and focus handling.
 */
export function EnquiryActionsMenu({
  label,
  items,
  disabled = false,
  onOpenChange,
}: {
  /** The trigger's accessible name — names the row, never a bare "Actions". */
  label: string;
  items: readonly EnquiryActionItem[];
  /** Another write is in flight somewhere on the list; a second would race it. */
  disabled?: boolean;
  /**
   * Told when the menu opens and when it closes — read off the DERIVED `open`,
   * so a menu closed by `disabled` or a busy item reports the close too.
   */
  onOpenChange?: (open: boolean) => void;
}) {
  const menuId = useId();
  const [requested, setOpen] = useState(false);
  const [rect, setRect] = useState({ left: 0, top: 0, width: MENU_WIDTH, flip: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const busy = items.some((item) => item.busy === true);
  /* Derived, so a trigger that becomes disabled while open leaves no menu behind. */
  const open = requested && !disabled && !busy;

  /* A LAYOUT effect, so a caller's state change on open lands before the first
     paint of the menu — otherwise an item reads its closed-state label for a
     frame. Only TRANSITIONS are reported; the initial closed state is not. */
  const onOpenChangeRef = useRef(onOpenChange);
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);
  const reportedOpen = useRef(false);
  useLayoutEffect(() => {
    if (reportedOpen.current === open) return;
    reportedOpen.current = open;
    onOpenChangeRef.current?.(open);
  }, [open]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(MENU_WIDTH, window.innerWidth - GUTTER * 2);
    // Measured once it exists; estimated for the very first placement.
    const height = menuRef.current?.offsetHeight ?? 8 + items.length * MENU_ITEM_ESTIMATE;
    const below = window.innerHeight - box.bottom;
    const flip = below < height + GUTTER && box.top > below;
    // Right-aligned to the trigger — it sits in the right-most column — then
    // clamped so it never hangs off a 320px screen.
    const maxLeft = Math.max(GUTTER, window.innerWidth - width - GUTTER);
    const left = Math.min(Math.max(box.right - width, GUTTER), maxLeft);
    setRect({ left, top: flip ? box.top : box.bottom, width, flip });
  }, [items.length]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    // A second pass once the menu has rendered, now against its real height.
    const frame = requestAnimationFrame(place);
    return () => cancelAnimationFrame(frame);
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    const elements = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    if (elements.length === 0) return;

    if (event.key === 'Tab') {
      // Portalled, so an uncaught Tab would strand focus at the end of the page.
      event.preventDefault();
      close();
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = elements.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? elements.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % elements.length
            : (current - 1 + elements.length) % elements.length;
    elements[next]?.focus();
  }

  if (items.length === 0) return null;

  return (
    <>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        loading={busy}
        disabled={disabled}
        trailingIcon={<ChevronDownIcon />}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
      >
        Actions
      </Button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            id={menuId}
            aria-label={label}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              ...(rect.flip
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.top + 4 }),
            }}
            className="z-[60] flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            {items.map((item) => (
              <Button
                key={item.key}
                role="menuitem"
                /* The variant carries the tone: a className colour beside a
                   variant's own colour races the stylesheet's emitted order. */
                variant={item.danger === true ? 'danger' : 'ghost'}
                size="sm"
                disabled={item.disabled === true}
                className="w-full justify-start"
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {item.label}
              </Button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * The "Create a contact" action a row may carry. The GATE is the caller's
 * (`ability.canCreate('contacts')`); `canCreate: false` withholds the item.
 */
export interface EnquiryContactAction {
  /** The enquiry's email — what the duplicate check looks for. */
  email: string | null;
  canCreate: boolean;
  /** Starts the create flow — find-or-create the company first when none is linked. */
  onCreate: () => void;
  busy?: boolean;
  /**
   * Whether the reader may LINK a company to this enquiry (the `triage` grant,
   * 2026-10-02). On a row with no company the flow links one first, so without
   * it the item is withheld there; a row that already has a company needs no
   * link. Omitted = no link step is ever needed (a submission always has one).
   */
  canLinkCompany?: boolean;
}

type ContactCheck =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'found'; id: number }
  /* "Not found" and "the read failed" share this: a failed check must not
     block creating, and the create flow checks again. */
  | { status: 'clear' };

/**
 * The duplicate check behind "Create a contact", run against the linked
 * company's contacts each time the menu OPENS — never cached across opens,
 * because a contact may have been created since.
 *
 * ⚠️ A CONVENIENCE, NOT A GUARANTEE: contact email is not unique server-side,
 * and a contact saved under another address, or between this check and a save,
 * is not seen. A check in flight disables the item rather than marking the
 * trigger busy — it is not a write.
 */
function useContactCheck(companyId: number | null, email: string | null) {
  const [check, setCheck] = useState<ContactCheck>({ status: 'idle' });
  const controller = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      controller.current?.abort();
      controller.current = null;
      if (!open || companyId === null || !isCheckableEmail(email)) {
        setCheck({ status: 'idle' });
        return;
      }
      const ctrl = new AbortController();
      controller.current = ctrl;
      setCheck({ status: 'checking' });
      findCompanyContactByEmail(companyId, email, ctrl.signal)
        .then((contact) => {
          if (ctrl.signal.aborted) return;
          setCheck(contact === null ? { status: 'clear' } : { status: 'found', id: contact.id });
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setCheck({ status: 'clear' });
        });
    },
    [companyId, email],
  );

  return { check, onOpenChange };
}

/**
 * The whole Actions cell: the one-line status links, the menu, and a per-row
 * failure beside it. Rendered by the table's pinned column AND the sub-`md`
 * card on all three sections, so no two layouts can disagree.
 */
export function EnquiryRowActions({
  rowName,
  company,
  deal,
  items,
  disabled = false,
  failure,
  contact,
}: {
  /** Names the row in the trigger's accessible name. */
  rowName: string;
  company?: { id: number; name: string | null } | null;
  deal?: EnquiryDealStatus | null;
  items: readonly EnquiryActionItem[];
  disabled?: boolean;
  failure?: string | null;
  /** Adds "Create a contact" / "Contact on file", after the `'company'` item. */
  contact?: EnquiryContactAction | null;
}) {
  const navigate = useNavigate();
  const companyId = company === null || company === undefined ? null : company.id;
  const offerContact =
    contact !== null &&
    contact !== undefined &&
    contact.canCreate &&
    (companyId !== null || contact.canLinkCompany !== false);
  const { check, onOpenChange } = useContactCheck(
    offerContact ? companyId : null,
    offerContact ? contact.email : null,
  );

  let menuItems: readonly EnquiryActionItem[] = items;
  if (offerContact) {
    const contactItem: EnquiryActionItem =
      check.status === 'checking'
        ? { key: 'contact', label: 'Checking contacts…', onSelect: () => {}, disabled: true }
        : check.status === 'found'
          ? {
              key: 'contact',
              label: 'Contact on file',
              onSelect: () => navigate(`/contacts/${check.id}`),
            }
          : {
              key: 'contact',
              label: 'Create a contact',
              onSelect: contact.onCreate,
              busy: contact.busy,
            };
    const at = items.findIndex((item) => item.key === 'company');
    menuItems =
      at === -1
        ? [contactItem, ...items]
        : [...items.slice(0, at + 1), contactItem, ...items.slice(at + 1)];
  }

  const companyLabel =
    company === null || company === undefined
      ? null
      : company.name !== null && company.name.trim() !== ''
        ? company.name
        : `Company #${company.id}`;

  return (
    <span className="flex min-w-0 flex-col items-start gap-1.5">
      {company !== null && company !== undefined && companyLabel !== null && (
        /* A real <Link>, never `Button href`, which reloads the whole bundle.
           Capped so a long school name truncates rather than widening the
           pinned column; the full name rides on the title. */
        <TextLink
          to={`/companies/${company.id}`}
          title={companyLabel}
          className="block max-w-36 truncate text-xs"
        >
          {companyLabel}
        </TextLink>
      )}

      {/* ⚠️ Two different facts; "Deal raised" is false about the second. */}
      {deal?.kind === 'raised' && (
        <Link
          to={`/deals/${deal.id}`}
          title={`Open deal #${deal.id}`}
          className="text-xs text-success whitespace-nowrap transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
        >
          Deal raised
        </Link>
      )}
      {deal?.kind === 'deleted' && <Badge tone="neutral">Deal since deleted</Badge>}

      <EnquiryActionsMenu
        label={`Actions for ${rowName}`}
        items={menuItems}
        disabled={disabled}
        onOpenChange={offerContact ? onOpenChange : undefined}
      />

      {/* Beside the menu that was used, never a panel-level banner. */}
      {failure !== null && failure !== undefined && (
        <span role="alert" className="text-xs text-danger [overflow-wrap:anywhere]">
          {failure}
        </span>
      )}
    </span>
  );
}
